import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API_URL, post } from "../api";
import { useAuth } from "../context/AuthContext";

// The Alert identity routes live at `/api/...`, not under the `/api/v2` API_URL.
const ALERT_API_BASE = API_URL.replace(/\/v\d+\/?$/, "");

/** Only navigate to a same-origin path; never to an arbitrary external URL. */
function safeDestination(destination?: string): string {
  if (!destination) return "/home";
  try {
    const u = new URL(destination, window.location.origin);
    if (u.origin === window.location.origin) {
      return u.pathname + u.search;
    }
  } catch {
    /* fall through to default */
  }
  return "/home";
}

/**
 * SDK realization Plan 008 — first-party Visaro SSO landing.
 *
 * Consumes the one-time handoff code from the Alert SDK's "Explore more on
 * Visaro" CTA, exchanges it for a normal Visaro session, removes the code from
 * history, and continues to the destination. The code is single-use and is NOT
 * a JWT; no bearer token ever sits in the URL.
 */
export default function PartnerSsoContinue() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const code = useMemo(() => searchParams.get("code")?.trim() ?? "", [searchParams]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!code) return;

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const res = await post(`${ALERT_API_BASE}/alert/identity/sso/redeem`, { code });
        // The redeem endpoint answers { status, token, destination } at the top
        // level (no `data` wrapper).
        const payload = (res?.data ?? res) as { token?: string; destination?: string } | undefined;

        if (!payload?.token) {
          throw new Error("Invalid response from server");
        }

        localStorage.setItem("token", payload.token);

        // Strip the one-time code from visible history immediately.
        if (typeof window !== "undefined") {
          window.history.replaceState({}, "", "/continue");
        }

        await refetch();
        if (cancelled) return;
        navigate(safeDestination(payload.destination), { replace: true });
      } catch (e: any) {
        if (cancelled) return;
        const message =
          typeof e?.message === "string" && e.message.trim().length > 0
            ? e.message
            : "This sign-in link has expired. Please return to the app and try again.";
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [code, navigate, refetch]);

  if (!code) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white p-6">
          <h1 className="text-xl font-semibold text-gray-900">Continue on Visaro</h1>
          <p className="mt-2 text-sm text-gray-600">
            This link is missing its code. Please return to the app and use the
            “Explore more on Visaro” option again.
          </p>
          <div className="mt-4">
            <Link className="text-visaro-orange font-semibold" to="/login">
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white p-6">
        <h1 className="text-xl font-semibold text-gray-900">Continue on Visaro</h1>
        {isLoading ? (
          <p className="mt-2 text-sm text-gray-600">Signing you in…</p>
        ) : errorMessage ? (
          <>
            <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
            <div className="mt-4">
              <Link className="text-visaro-orange font-semibold" to="/login">
                Go to login
              </Link>
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-600">Redirecting…</p>
        )}
      </div>
    </div>
  );
}
